import { responseSchemaFor } from '@upright/common/zod-utils';
import { z } from 'zod';
import { AccessLevel } from '../types';
import { EntityTypeForAccessDetails, AccessDetails, DataSetId } from './types';

export type GetAccessDetailsRequest<T extends EntityTypeForAccessDetails = EntityTypeForAccessDetails> = {
  entityId: string;
  /**
   * Optional: Used for internal on-demand modes
   */
  jobId?: number;
  entityType: T;
  release: string | undefined | null;
  // Only for companies. TODO: Maybe separate the types to reflects this.
  scenarioId?: string;

  // For now we don't support querying with start and end dates, as that is likely never used
  // from our UI.
};

export type ViewAccess = 'library' | 'dma';

export type GetAccessDetailsResponse<T extends EntityTypeForAccessDetails = EntityTypeForAccessDetails> = {
  entityId: string;
  entityType: T;
  release: string | undefined | null;
  details: AccessDetails<T>;
  accessLevelToEntity: AccessLevel | undefined;
  accessToViews: ViewAccess[];
  /**
   * PLT-3933 Scenario-3 mitigation: `true` when the server resolved the request
   * to an authenticated user, `false` when it served the anonymous path. The SPA
   * uses this to detect the case where Redux still believes the user is logged
   * in but the server saw the request as anonymous (e.g. both auth cookies were
   * dropped client-side, so no `up-refresh` was sent, so the backend's 401
   * guard cannot fire) and force a fresh login instead of rendering a stuck
   * "logged-in but no data" state.
   */
  viewerIsAuthenticated: boolean;
};

const zSingleDataSetAccessDetails = z.object({
  isAccessibleToUser: z.boolean(),
  // Absent from the wire whenever the dataset is inaccessible (the server sets it to
  // `undefined`, which `JSON.stringify` drops), so presence cannot be required here.
  isDataAvailable: z.boolean().optional(),
});

/**
 * Every entry is optional, including the four datasets that all three of today's builders emit
 * unconditionally. What this schema is here to catch is a dataset entry whose *shape* changed —
 * `isAccessibleToUser` no longer a boolean, the object reshaped — and `.optional()` catches that
 * just as well. Requiring presence buys nothing on top of it and costs the whole platform on the
 * day a new entity type ships with a smaller set, or a tier stops carrying `euSfdr`: the response
 * is rejected app-wide and every gate falls back to hiding.
 */
const zAccessDetails = z.object({
  [DataSetId.netImpact]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.sdgAlignment]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.euTaxonomy]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.euSfdr]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.csrd]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.financialEffects]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.climateRisk]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.insights]: zSingleDataSetAccessDetails.optional(),
  [DataSetId.targets]: zSingleDataSetAccessDetails.optional(),
});

/**
 * Response guard for the access-details endpoints, checked at the fetch boundary (see
 * `fetchAndValidateOrThrow`).
 *
 * Every gate in the platform reads this response, and the fallback for a *missing* answer is to
 * hide: a payload that arrives with `isAccessibleToUser` no longer a boolean, or with the
 * per-dataset objects reshaped, resolves to a falsy access decision and paywalls a customer who
 * paid for the data — silently, and identically to a legitimate "no access" answer. Failing
 * outright is the recoverable outcome, since the query's error state is what the
 * relogin/error handling in `useGetAccessDetails` already reacts to.
 *
 * `entityType`, `accessLevelToEntity` and `accessToViews` are validated as strings rather than
 * as their enums: adding an access level or a view is an additive backend change, and the UI
 * compares these against known values instead of exhausting them.
 */
const zGetAccessDetailsResponseSchema = z.object({
  entityId: z.string(),
  entityType: z.string(),
  details: zAccessDetails,
  accessLevelToEntity: z.string().optional(),
  accessToViews: z.array(z.string()),
  viewerIsAuthenticated: z.boolean(),
});
export const zGetAccessDetailsResponse = responseSchemaFor<
  GetAccessDetailsResponse,
  typeof zGetAccessDetailsResponseSchema
>(zGetAccessDetailsResponseSchema);
